import { Box, ComboboxKeyboardEvents, Divider, Menu, MenuItem, styled, Typography } from '@chronosphereio/chrono-ui';
import { Close as CloseIcon } from 'mdi-material-ui';
import React, { forwardRef, isValidElement, ReactElement, ReactNode, useId, useState } from 'react';

const { shouldOpenCombobox } = ComboboxKeyboardEvents;

function flattenChildren(children: ReactNode): ReactElement[] {
  const items: ReactElement[] = [];

  React.Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === React.Fragment) {
      items.push(...flattenChildren(child.props.children));
    } else {
      items.push(child);
    }
  });

  return items;
}

export function GroupRoot({
  size,
  onRemove,
  children,
}: {
  size?: 'small' | 'default';
  onRemove?: () => void;
  children: ReactNode;
}) {
  const items = flattenChildren(children);

  return (
    <GroupContainer size={size}>
      {items.map((child, i) => {
        const showDivider = i < items.length - 1;

        return (
          <React.Fragment key={i}>
            {child}
            {showDivider && <Divider orientation="vertical" flexItem />}
          </React.Fragment>
        );
      })}

      {onRemove && (
        <>
          <Divider orientation="vertical" flexItem />
          <Group.Button onClick={onRemove} isLast>
            <CloseIcon fontSize="inherit" />
          </Group.Button>
        </>
      )}
    </GroupContainer>
  );
}

function GroupContainer({ size = 'default', children }: { size?: 'small' | 'default'; children: ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        display: 'inline-flex',
        flexDirection: 'row',
        borderRadius: '4px',
        alignItems: 'center',
        border: `1px solid ${theme.palette.border.bold}`,
        overflow: 'hidden',
        height: size === 'small' ? 28 : 32,
        width: 'fit-content',
      })}
    >
      {children}
    </Box>
  );
}

function GroupLabel({ children, isMuted = false }: { children: ReactNode; isMuted?: boolean }) {
  return (
    <Box
      sx={(theme) => ({
        padding: theme.spacing(0.5, 1),
        backgroundColor: theme.palette.background[isMuted ? 'default' : 'canvas'],
        color: theme.palette.text.secondary,
        fontSize: theme.typography.body1.fontSize,
      })}
    >
      {children}
    </Box>
  );
}

const GroupButtonBase = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isPlaceholder' && prop !== 'isFirst' && prop !== 'isLast',
})<{ isPlaceholder?: boolean; isFirst?: boolean; isLast?: boolean; isSolo?: boolean }>(
  ({ theme, isPlaceholder, isFirst, isLast, isSolo }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1),
    border: 0,
    backgroundColor: theme.palette.background.default,
    color: isPlaceholder ? theme.palette.text.disabled : theme.palette.text.primary,
    fontFamily: theme.typography.body1.fontFamily,
    height: '100%',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: theme.palette.background.hover,
      color: theme.palette.text.primary,
      cursor: 'pointer',
    },
    '&:focus': {
      border: `1px solid ${theme.palette.primary.main}`,
      outline: `1px solid ${theme.palette.primary.main}`,
      // Left side
      borderTopLeftRadius: isFirst || isSolo ? '4px' : 0,
      borderBottomLeftRadius: isFirst || isSolo ? '4px' : 0,
      // Right side
      borderTopRightRadius: isLast || isSolo ? '4px' : 0,
      borderBottomRightRadius: isLast || isSolo ? '4px' : 0,
    },
  })
);

type GroupButtonProps = {
  isPlaceholder?: boolean;
  isSolo?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const GroupButton = forwardRef<HTMLButtonElement, GroupButtonProps>((props, ref) => (
  <GroupButtonBase
    ref={ref}
    // Make sure type="button" to prevent form submission
    type="button"
    {...props}
  />
));

GroupButton.displayName = 'GroupButton';

type GroupButtonMenuOption = {
  value: string;
  label: string | ReactNode;
};

/**
 * Meant to be a basic menu that behaves similarly to `ComboboxSelect`.
 * This creates a cohesive experience when using a `Group` component with both `GroupButtonMenu` and `ComboboxSelect`.
 * Basic means no search interface, no virtualization, and it only selects a single item.
 */
const GroupButtonMenu = ({
  value,
  label,
  options,
  onSelect,
}: {
  value: string;
  label: string | ReactNode;
  options: GroupButtonMenuOption[];
  onSelect: (value: string) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const open = Boolean(anchorEl);
  const openMenu = (e: React.MouseEvent | React.KeyboardEvent) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const menuId = `menu-${useId()}`;

  return (
    <>
      <Group.Button
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={openMenu}
        onKeyDown={(e) => {
          if (shouldOpenCombobox(e)) openMenu(e);
        }}
      >
        {label}
      </Group.Button>
      <Menu id={menuId} open={open} anchorEl={anchorEl} onClose={closeMenu}>
        {options.map((o) => (
          <MenuItem
            key={o.value}
            selected={o.value === value}
            onClick={() => {
              onSelect(o.value);
              closeMenu();
            }}
            sx={{
              '&.Mui-selected': {
                backgroundColor: (t) => t.palette.background.selected,
                '&:hover, &:focus': {
                  backgroundColor: (t) => t.palette.background.hover,
                },
              },
              '&:hover, &:focus': {
                backgroundColor: (t) => t.palette.background.hover,
              },
            }}
          >
            <Typography variant="body2">{o.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export const Group = {
  Root: GroupRoot,
  Label: GroupLabel,
  Button: GroupButton,
  ButtonMenu: GroupButtonMenu,
};
